import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import ActionsMenu from "./ActionsMenu";

const emptyForm = {
  fee_type_id: "",
  fee_category_id: "",
  academic_year: "",
  fee_head_name: "",
  fee_amount: "",
  fee_head_description: "",
  start_date: "",
  end_date: "",
  status: "ACTIVE",
  enroll_type: "AUTO",
  pay_type: "FULL",
  workflow_status: "DRAFT",
};

const pageSizeOptions = [10, 20, 50];

export default function FeeHead() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingFeeHeadId, setEditingFeeHeadId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const isEditing = Boolean(editingFeeHeadId);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const mapFeeHeadRow = (item) => ({
    ...item,
    id: item.fee_head_id,
    fee_type_name: item.feeType?.fee_type_name || "",
    fee_category_id: item.feeType?.fee_category_id || "",
    start_date: item.start_date ? item.start_date.slice(0, 10) : "",
    end_date: item.end_date ? item.end_date.slice(0, 10) : "",
  });

  const fetchFeeHeads = async (gridPage = page, gridPageSize = pageSize, gridSearch = search) => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(gridPage + 1),
        pageSize: String(gridPageSize),
        search: gridSearch,
      });
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-heads/${clientId}/${collegeId}?${params.toString()}`
      );
      const payload = data?.data || {};
      const items = Array.isArray(payload) ? payload : payload.items || [];
      setRows(items.map(mapFeeHeadRow));
      setRowCount(Array.isArray(payload) ? items.length : payload.total || 0);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Unable to load fee heads", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAcademicYears = async () => {
    if (!clientId || !collegeId) return;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/acyear/by-college/${clientId}/${collegeId}`
      );
      setAcademicYears(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setAcademicYears([]);
      showSnackbar(error.response?.data?.message || "Unable to load academic years", "error");
    }
  };

  const fetchCategories = async () => {
    if (!clientId || !collegeId) return;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-categories/${clientId}/${collegeId}`
      );
      setCategories(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Unable to load fee categories", "error");
    }
  };

  const fetchFeeTypes = async (feeCategoryId) => {
    if (!clientId || !collegeId || !feeCategoryId) {
      setFeeTypes([]);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-types/${clientId}/${collegeId}/${feeCategoryId}`
      );
      setFeeTypes(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setFeeTypes([]);
      showSnackbar(error.response?.data?.message || "Unable to load fee types", "error");
    }
  };

  const fetchCollege = async () => {
    if (!clientId || !collegeId) return;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/colleges/college/${clientId}/${collegeId}`
      );
      setCollege(data?.data || null);
    } catch (error) {
      setCollege(null);
      showSnackbar(error.response?.data?.message || "Unable to load college", "error");
    }
  };

  const buildFeeHeadName = (academicYear, feeTypeId) => {
    const selectedFeeType = feeTypes.find((type) => String(type.fee_type_id) === String(feeTypeId));
    const collegeCode = String(college?.college_code || "").trim();
    const feeTypeName = String(selectedFeeType?.fee_type_name || "").trim();

    if (!academicYear || !collegeCode || !feeTypeName) return "";
    return `${academicYear}-${collegeCode}-${feeTypeName}`.toUpperCase();
  };

  useEffect(() => {
    fetchCategories();
    fetchAcademicYears();
    fetchCollege();
  }, [clientId, collegeId]);

  useEffect(() => {
    fetchFeeHeads(page, pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, collegeId, page, pageSize, search]);

  useEffect(() => {
    fetchFeeTypes(form.fee_category_id);
  }, [form.fee_category_id]);

  useEffect(() => {
    if (isEditing) return;

    const feeHeadName = buildFeeHeadName(form.academic_year, form.fee_type_id);
    if (feeHeadName && feeHeadName !== form.fee_head_name) {
      setForm((prev) => ({
        ...prev,
        fee_head_name: feeHeadName,
        fee_head_description: prev.fee_head_description || feeHeadName,
      }));
    }
  }, [isEditing, form.academic_year, form.fee_type_id, feeTypes, college]);

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingFeeHeadId("");
    setForm(emptyForm);
  };

  const handleOpenAdd = () => {
    setEditingFeeHeadId("");
    setForm(emptyForm);
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    const feeHeadId = row.fee_head_id || row.id;
    setEditingFeeHeadId(feeHeadId);
    setForm({
      fee_type_id: row.fee_type_id || "",
      fee_category_id: row.fee_category_id || "",
      academic_year: row.academic_year || "",
      fee_head_name: row.fee_head_name || "",
      fee_amount: row.fee_amount || "",
      fee_head_description: row.fee_head_description || "",
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      status: row.status || "ACTIVE",
      enroll_type: row.enroll_type || "AUTO",
      pay_type: row.pay_type || "FULL",
      workflow_status: row.workflow_status || "DRAFT",
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the fee head from active setup.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/fee-heads/${clientId}/${collegeId}/${row.fee_head_id}`
      );
      setRows((prevRows) =>
        prevRows.filter((item) => String(item.fee_head_id) !== String(row.fee_head_id))
      );
      setRowCount((prevCount) => Math.max(prevCount - 1, 0));
      await Swal.fire("Deleted", "Fee head deleted successfully.", "success");
    } catch (error) {
      await Swal.fire(
        "Delete failed",
        error.response?.data?.message || "Unable to delete fee head",
        "error"
      );
    }
  };

  const handleSave = async () => {
    if (!form.fee_type_id || !form.academic_year || !form.fee_head_name || !form.fee_amount) {
      showSnackbar("Fee type, academic year, fee head name and amount are required", "warning");
      return;
    }

    const targetFeeHeadId = editingFeeHeadId;
    const payload = {
      ...form,
      fee_head_id: targetFeeHeadId ? String(targetFeeHeadId) : undefined,
      college_id: Number(collegeId),
      fee_type_id: String(form.fee_type_id),
      fee_amount: Number(form.fee_amount),
      created_by: user?.user_email || "",
      modified_by: user?.user_email || "",
    };

    try {
      let response;
      if (targetFeeHeadId) {
        response = await axios.put(
          `${API_BASE_URL}/api/fee-heads/${clientId}/${collegeId}/${targetFeeHeadId}`,
          payload
        );
        const updatedRow = mapFeeHeadRow(response.data?.data || {});
        setRows((prevRows) =>
          prevRows.map((row) =>
            String(row.fee_head_id) === String(targetFeeHeadId)
              ? { ...row, ...updatedRow, id: targetFeeHeadId }
              : row
          )
        );
        showSnackbar("Fee head updated successfully");
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/fee-heads/${clientId}/${collegeId}`,
          payload
        );
        const createdRow = mapFeeHeadRow(response.data?.data || {});
        setRows((prevRows) => [createdRow, ...prevRows].slice(0, pageSize));
        setRowCount((prevCount) => prevCount + 1);
        showSnackbar("Fee head created successfully");
      }
      setOpen(false);
      setEditingFeeHeadId("");
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Unable to save fee head", "error");
    }
  };

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "fee_head_name", headerName: "Fee Head", minWidth: 180, flex: 1.2 },
    { field: "fee_type_name", headerName: "Fee Type", minWidth: 140, flex: 1 },
    { field: "academic_year", headerName: "Academic Year", minWidth: 120, flex: 0.8 },
    { field: "fee_amount", headerName: "Amount", minWidth: 110, flex: 0.7 },
    { field: "status", headerName: "Status", minWidth: 100, flex: 0.7 },
    { field: "start_date", headerName: "Start Date", minWidth: 120, flex: 0.8 },
    { field: "end_date", headerName: "End Date", minWidth: 120, flex: 0.8 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      minWidth: 120,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleOpenEdit} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Fee Heads
          </Typography>
          <TextField
            size="medium"
            placeholder="Search..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ color: "#fff" }}
          >
            Add Fee Head
          </Button>
        </Toolbar>

        <Box
          mt={2}
          sx={{
            height: "calc(100vh - 230px)",
            minHeight: 420,
            width: "100%",
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={pageSizeOptions}
            disableSelectionOnClick
            sx={{
              border: "1px solid #e0e0e0",
              "& .MuiDataGrid-cell": {
                alignItems: "center",
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }

          handleCloseDialog();
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isEditing ? "Edit Fee Head" : "Add Fee Head"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Fee Category"
                value={form.fee_category_id}
                onChange={(event) =>
                  setForm({ ...form, fee_category_id: event.target.value, fee_type_id: "" })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Fee Category</option>
                {categories.map((category) => (
                  <option key={category.fee_category_id} value={category.fee_category_id}>
                    {category.fee_category_name}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="Fee Type"
                value={form.fee_type_id}
                onChange={(event) => setForm({ ...form, fee_type_id: event.target.value })}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Fee Type</option>
                {feeTypes.map((type) => (
                  <option key={type.fee_type_id} value={type.fee_type_id}>
                    {type.fee_type_name}
                  </option>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Academic Year"
                select
                value={form.academic_year}
                onChange={(event) => setForm({ ...form, academic_year: event.target.value })}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Academic Year</option>
                {academicYears.map((year) => (
                  <option key={year.acyear_id} value={year.academic_year}>
                    {year.academic_year}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Fee Amount"
                type="number"
                value={form.fee_amount}
                onChange={(event) => setForm({ ...form, fee_amount: event.target.value })}
                required
                fullWidth
              />
            </Stack>

            <TextField
              label="Fee Head Name"
              value={form.fee_head_name}
              InputProps={{ readOnly: true }}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={form.fee_head_description}
              onChange={(event) => setForm({ ...form, fee_head_description: event.target.value })}
              multiline
              minRows={2}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={form.start_date}
                onChange={(event) => setForm({ ...form, start_date: event.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={form.end_date}
                onChange={(event) => setForm({ ...form, end_date: event.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(event) => setForm({ ...form, status: event.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </TextField>
              <TextField
                select
                label="Enroll Type"
                value={form.enroll_type}
                onChange={(event) => setForm({ ...form, enroll_type: event.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="AUTO">AUTO</option>
                <option value="SELF">SELF</option>
                <option value="MANUAL">MANUAL</option>
              </TextField>
              <TextField
                select
                label="Pay Type"
                value={form.pay_type}
                onChange={(event) => setForm({ ...form, pay_type: event.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="FULL">FULL</option>
                <option value="PART">PART</option>
                <option value="PARTIAL">PARTIAL</option>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} sx={{ color: "#fff" }}>
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
